import { Box, css, LinearProgress, styled, svgIconClasses, Typography } from '@mui/material';
import { orange, red } from '@mui/material/colors';
import React from 'react';
import { filesize } from '~/lib/file';
import withProps from '~/lib/reactTools/withProps';
import AutoTitle from '../AutoTitle';
import CloseButton from '../CloseButton';
import FileIcon from '../FileIcon';
import RefreshButton from '../RefreshButton';

export interface UploadStatusProps {
  id: string;
  filename: string;
  filetype: string;
  size: number;
  done: boolean;
  progress: number;
  onRemove?: (id: string) => void;
  onReview?: (id: string) => void;
  onRetry?: (id: string) => void;
  error?: string;
}

const Container = styled(Box)`
  display: flex;
  align-items: center;
  border-radius: calc(${({ theme }) => theme.shape.borderRadius}px * 0.6);
  width: 100%;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  transition: 200ms;

  &:hover {
    background-color: ${({ theme }) => theme.palette.background.default};
  }
`;

const Icon = styled(FileIcon)`
  height: 2rem;
  margin-right: 0.5rem;
`;

const Filename = styled(Typography)`
  font-size: 0.8rem;
  font-weight: 500;
`;

const SecondaryLabel = styled(Typography)`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-weight: 500;
`;

const Progress = styled(LinearProgress)`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const LabelProgressContainer = styled(Box)`
  flex-grow: 1;
  margin: 0 ${({ theme }) => theme.spacing(1)};
`;

const LabelPercentageContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorMessage = styled(SecondaryLabel)`
  color: ${red[300]};
`;

const controlButtonStyle = css`
  width: 1rem;
  height: 1rem;

  .${svgIconClasses.root} {
    color: white;
    width: 0.7rem;
  }
`;

const RemoveButton = styled(withProps(CloseButton, { disableRipple: true }))`
  ${controlButtonStyle}

  margin-left: ${({ theme }) => theme.spacing(0.5)};

  &,
  &:hover {
    background-color: ${red[300]};
  }
`;

const RetryButton = styled(withProps(RefreshButton, { disableRipple: true }))`
  ${controlButtonStyle}

  &,
  &:hover {
    background-color: ${orange[300]};
  }
`;

const UploadStatus: React.FC<UploadStatusProps> = ({
  id,
  filename,
  filetype,
  size,
  progress,
  done,
  onRemove,
  onReview,
  onRetry,
  error
}) => {
  const handleReview = () => {
    if (done) {
      onReview?.(id);
    }
  };

  const handleRetry = () => onRetry?.(id);

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.(id);
  };

  return (
    <Container onClick={handleReview} sx={{ cursor: done ? 'pointer' : 'default' }}>
      <Icon mimeType={filetype} />

      <LabelProgressContainer>
        <LabelPercentageContainer>
          <AutoTitle title={filename}>
            <Filename>{filename}</Filename>
          </AutoTitle>
          {!done && !error && <SecondaryLabel>{Math.trunc(progress * 100)}%</SecondaryLabel>}
        </LabelPercentageContainer>

        {done ? (
          <SecondaryLabel>{filesize(size)}</SecondaryLabel>
        ) : error ? (
          <AutoTitle title={error}>
            <ErrorMessage>{error}</ErrorMessage>
          </AutoTitle>
        ) : (
          <Progress variant={progress > 0 ? 'determinate' : 'indeterminate'} value={progress * 100} />
        )}
      </LabelProgressContainer>

      {error && <RetryButton onClick={handleRetry} />}
      <RemoveButton onClick={handleRemove} />
    </Container>
  );
};

export default UploadStatus;
